/******************************************************************************
 *
 * Copyright (c) 2019-2020 ServiceActivator Inc. All Rights Reserved.
 * 
 * Unauthorized copying of the code in this file, completely or partially, 
 * via any medium is strictly prohibited.
 *
 * Proprietary and Confidential.
 * 
 * Written by 'Vishal Goel' <vishal@wakencode.com>, May 2020
 *
 *****************************************************************************/


import React from "react";
import { InfoBox } from "@react-google-maps/api";
import { useSelector, shallowEqual } from "react-redux";
import Warning from "@material-ui/icons/Warning";
import { constant } from "./../../../shared/constants/constants";
import { makeStyles } from "@material-ui/core/styles";
import {
  filterByJobType,
  filterByUserJob,
  getNameInitials,
} from "./../../../shared/geofence";
import Tooltip from "./../../../Tooltip/Tooltip";

const useStyles = makeStyles((theme) => ({
  usrTxt: {
    fontSize: 16,
    color: "#fff",
    height: "40px",
    width: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  usrImg: {
    height: "40px",
    width: "40px",
  },
  errorIcon: {
    color: "grey",
    fontSize: "35px",
    height: "40px",
    width: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

// to avoid overlapping of user initials
const precisionIncrement = 0.00007;

// show assigned users by filtering with job type and individual user
const WorkOrderInfo = () => {
  const properties = useSelector(
    (state) => state.geofence.geofenceData,
    shallowEqual
  );
  const isVisible = useSelector(
    (state) => state.geoUtil.showUserInfoBox,
    shallowEqual
  );
  const jobType = useSelector((state) => state.geoUtil.jobType, shallowEqual);
  const user = useSelector((state) => state.geoUtil.user, shallowEqual);
  const classes = useStyles();

  let info = [];
  if (isVisible) {
    let dataToBeShown = [];

    // if no job type filter is selected
    if (jobType === "") {
      for (let property of properties) {
        let precision = 0.0;
        // to avoid multiple plotation of user
        let alreadyPresent = {};
        for (let poly of property.workOrders) {
          if (poly.userNameId) {
            if (alreadyPresent[poly.userNameId]) continue;
            alreadyPresent[poly.userNameId] = true;
          } else {
            if (alreadyPresent["unassigned"]) continue;
            alreadyPresent["unassigned"] = true;
          }

          precision = precision + precisionIncrement;
          info.push(
            <InfoBox
              key={poly.workOrderId}
              position={{
                lat: property.center.lat,
                lng: property.center.lng - precision,
              }}
              zIndex={1000}
              options={{
                pane: "overlayLayer",
                alignBottom: true,
                boxStyle: {
                  boxShadow: `3px 3px 10px rgba(0,0,0,0.6)`,
                  borderRadius: "50%",
                },
                closeBoxURL: "",
              }}>
              <div>
                {poly.userNameId && (
                  <Tooltip arrow title={poly.userName}>
                    {global.userColor[poly.userNameId] &&
                    global.userColor[poly.userNameId].photoUrl ? (
                      <img
                        className={classes.usrImg}
                        src={global.userColor[poly.userNameId].photoUrl}
                        alt={poly.nickName}
                      />
                    ) : (
                      <p
                        className={classes.usrTxt}
                        style={{
                          backgroundColor: global.userColor[poly.userNameId]
                            ? global.userColor[poly.userNameId].colorCode
                            : poly.nickBg,
                        }}>
                        {poly.nickName}
                      </p>
                    )}
                  </Tooltip>
                )}

                {!poly.userNameId && (
                  <Tooltip
                    title={constant.NO_TECHNICIAN_ASSIGNED}
                    style={{ color: "#F28E1A", fontSize: "35px" }}>
                    <Warning color="inherit" fontSize="inherit" />
                  </Tooltip>
                )}
              </div>
            </InfoBox>
          );
        }
      }
    }
    // if "unassigned" job type is selected
    else if (jobType === "unassigned_jobs") {
      dataToBeShown = filterByJobType(properties, jobType);
      for (let property of dataToBeShown) {
        // to avoid multiple plotation of user
        let alreadyPresent = {};
        for (let poly of property.workOrders) {
          if (poly.userNameId) continue;

          if (alreadyPresent["unassigned"]) continue;
          alreadyPresent["unassigned"] = true;

          info.push(
            <InfoBox
              key={poly.workOrderId}
              position={{
                lat: property.center.lat,
                lng: property.center.lng,
              }}
              zIndex={1000}
              options={{
                pane: "overlayLayer",
                alignBottom: true,
                boxStyle: {
                  boxShadow: `3px 3px 10px rgba(0,0,0,0.6)`,
                  borderRadius: "50%",
                },
                closeBoxURL: "",
              }}>
              <div>
                <Tooltip
                  title={constant.NO_TECHNICIAN_ASSIGNED}
                  style={{
                    color: "#F28E1A",
                    fontSize: "35px",
                  }}>
                  <Warning color="inherit" fontSize="inherit" />
                </Tooltip>
              </div>
            </InfoBox>
          );
        }
      }
    }
    // if "technician" job type is selected
    else if (jobType === "technician_jobs") {
      // if also user filter is used
      if (user.userId) {
        dataToBeShown = filterByUserJob(properties, user.userId);
        for (let property of dataToBeShown) {
          for (let poly of property.geometries) {
            info.push(
              <InfoBox
                key={poly.gid}
                position={{
                  lat: poly.center.lat,
                  lng: poly.center.lng,
                }}
                zIndex={1000}
                options={{
                  pane: "overlayLayer",
                  alignBottom: true,
                  boxStyle: {
                    boxShadow: `3px 3px 10px rgba(0,0,0,0.6)`,
                    borderRadius: "50%",
                  },
                  closeBoxURL: "",
                }}>
                <div>
                  <Tooltip arrow title={user.name}>
                    {global.userColor[user.userId] &&
                    global.userColor[user.userId].photoUrl ? (
                      <img
                        className={classes.usrImg}
                        src={global.userColor[user.userId].photoUrl}
                        alt={getNameInitials(user.name)}
                      />
                    ) : (
                      <p
                        className={classes.usrTxt}
                        style={{
                          backgroundColor: global.userColor[user.userId]
                            ? global.userColor[user.userId].colorCode
                            : "#8FA225",
                        }}>
                        {getNameInitials(user.name)}
                      </p>
                    )}
                  </Tooltip>
                </div>
              </InfoBox>
            );
          }
        }
      } else {
        // if user filter is not used
        dataToBeShown = filterByJobType(properties, jobType);
        for (let property of dataToBeShown) {
          let alreadyPresent = {};
          let precision = 0.0;

          for (let poly of property.workOrders) {
            if (poly.userType !== "technician") continue;

            if (alreadyPresent[poly.userNameId]) continue;
            alreadyPresent[poly.userNameId] = true;

            precision = precision + precisionIncrement;

            info.push(
              <InfoBox
                key={poly.workOrderId}
                position={{
                  lat: property.center.lat,
                  lng: property.center.lng - precision,
                }}
                zIndex={1000}
                options={{
                  pane: "overlayLayer",
                  alignBottom: true,
                  boxStyle: {
                    boxShadow: `3px 3px 10px rgba(0,0,0,0.6)`,
                    borderRadius: "50%",
                  },
                  closeBoxURL: "",
                }}>
                <div>
                  {poly.userNameId && (
                    <Tooltip arrow title={poly.userName}>
                      {global.userColor[poly.userNameId] &&
                      global.userColor[poly.userNameId].photoUrl ? (
                        <img
                          className={classes.usrImg}
                          src={global.userColor[poly.userNameId].photoUrl}
                          alt={poly.nickName}
                        />
                      ) : (
                        <p
                          className={classes.usrTxt}
                          style={{
                            backgroundColor: global.userColor[poly.userNameId]
                              ? global.userColor[poly.userNameId].colorCode
                              : poly.nickBg,
                          }}>
                          {poly.nickName}
                        </p>
                      )}
                    </Tooltip>
                  )}
                </div>
              </InfoBox>
            );
          }
        }
      }
    }
    // if "contractor" job type is selected
    else if (jobType === "contractor_jobs") {
      // if also user filter is used
      if (user.userId) {
        dataToBeShown = filterByUserJob(properties, user.userId);
        for (let property of dataToBeShown) {
          for (let poly of property.geometries) {
            info.push(
              <InfoBox
                key={poly.gid}
                position={{
                  lat: poly.center.lat,
                  lng: poly.center.lng,
                }}
                zIndex={1000}
                options={{
                  pane: "overlayLayer",
                  alignBottom: true,
                  boxStyle: {
                    boxShadow: `3px 3px 10px rgba(0,0,0,0.6)`,
                    borderRadius: "50%",
                  },
                  closeBoxURL: "",
                }}>
                <div>
                  <Tooltip arrow title={user.name}>
                    {global.userColor[user.userId] &&
                    global.userColor[user.userId].photoUrl ? (
                      <img
                        className={classes.usrImg}
                        src={global.userColor[user.userId].photoUrl}
                        alt={getNameInitials(user.name)}
                      />
                    ) : (
                      <p
                        className={classes.usrTxt}
                        style={{
                          backgroundColor: global.userColor[user.userId]
                            ? global.userColor[user.userId].colorCode
                            : "#8FA225",
                        }}>
                        {getNameInitials(user.name)}
                      </p>
                    )}
                  </Tooltip>
                </div>
              </InfoBox>
            );
          }
        }
      }
      // if user filter is not used
      else {
        dataToBeShown = filterByJobType(properties, jobType);
        for (let property of dataToBeShown) {
          let alreadyPresent = {};
          let precision = 0.0;
          for (let poly of property.workOrders) {
            if (poly.userType !== "contractor") continue;

            if (alreadyPresent[poly.userNameId]) continue;
            alreadyPresent[poly.userNameId] = true;

            precision = precision + precisionIncrement;

            info.push(
              <InfoBox
                key={poly.workOrderId}
                position={{
                  lat: property.center.lat,
                  lng: property.center.lng - precision,
                }}
                zIndex={1000}
                options={{
                  pane: "overlayLayer",
                  alignBottom: true,
                  boxStyle: {
                    boxShadow: `3px 3px 10px rgba(0,0,0,0.6)`,
                    borderRadius: "50%",
                  },
                  closeBoxURL: "",
                }}>
                <div>
                  {poly.userNameId && (
                    <Tooltip arrow title={poly.userName}>
                      {global.userColor[poly.userNameId] &&
                      global.userColor[poly.userNameId].photoUrl ? (
                        <img
                          className={classes.usrImg}
                          src={global.userColor[poly.userNameId].photoUrl}
                          alt={poly.nickName}
                        />
                      ) : (
                        <p
                          className={classes.usrTxt}
                          style={{
                            backgroundColor: global.userColor[poly.userNameId]
                              ? global.userColor[poly.userNameId].colorCode
                              : poly.nickBg,
                          }}>
                          {poly.nickName}
                        </p>
                      )}
                    </Tooltip>
                  )}
                </div>
              </InfoBox>
            );
          }
        }
      }
    }
  }

  return info;
};

export default React.memo(WorkOrderInfo);
